import { EnvelopeSimple } from '@phosphor-icons/react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router';
import { forgotPasswordInput } from '@brechoflow/contracts';
import type { z } from 'zod';
import { Button } from '@/components/ui/button';
import { FormField } from '@/components/ui/form-field';
import { Input } from '@/components/ui/input';
import { errorMessage } from '@/lib/api';
import { contractResolver } from '@/lib/forms';
import { authApi } from '../auth-api';

type Values = z.input<typeof forgotPasswordInput>;

export function ForgotPasswordPage() {
  const [sent, setSent] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Values, unknown, z.output<typeof forgotPasswordInput>>({ resolver: contractResolver(forgotPasswordInput), defaultValues: { email: '' } });

  const onSubmit = handleSubmit(async ({ email }) => {
    setFormError(null);
    try {
      const r = await authApi.forgotPassword(email);
      setSent(r.message);
    } catch (e) {
      setFormError(errorMessage(e));
    }
  });

  if (sent) {
    return (
      <div role="status">
        <span className="flex size-12 items-center justify-center rounded-panel bg-sand text-sand-foreground">
          <EnvelopeSimple className="size-6" weight="duotone" aria-hidden />
        </span>
        <h1 className="mt-5 font-display text-3xl font-semibold">Confira seu e-mail</h1>
        <p className="mt-2 text-sm text-muted-foreground">{sent}</p>
        <Button asChild variant="secondary" className="mt-6">
          <Link to="/entrar">Voltar para entrar</Link>
        </Button>
      </div>
    );
  }

  return (
    <>
      <h1 className="font-display text-3xl font-semibold">Recuperar senha</h1>
      <p className="mt-1.5 text-sm text-muted-foreground">Informe o e-mail da conta. Enviaremos um link para criar uma nova senha.</p>
      <form onSubmit={onSubmit} noValidate className="mt-7 flex flex-col gap-4">
        {formError && (
          <p role="alert" className="rounded-control bg-destructive-soft px-3 py-2.5 text-sm text-destructive">
            {formError}
          </p>
        )}
        <FormField label="E-mail" error={errors.email?.message}>
          {(a) => <Input type="email" autoComplete="username" autoFocus {...a} {...register('email')} />}
        </FormField>
        <Button type="submit" size="lg" loading={isSubmitting}>
          Enviar link
        </Button>
      </form>
      <p className="mt-6 text-sm">
        <Link to="/entrar" className="text-primary underline-offset-4 hover:underline">
          Voltar para entrar
        </Link>
      </p>
    </>
  );
}
